import { postCheckEmail, postCheckName } from 'api/user.request'
import { Button, Input, Password } from 'components/shared'
import { useConfirmV2 } from 'contexts/ConfirmV2Provider'
import { Register } from 'interfaces/user.interface'
import { useState } from 'react'
import { classNameJoin, isValidNickname, validatePassword } from 'utils/lib'
import { spaceRegex } from 'utils/regexs'

interface StepOneProps {
    item: Register
    callback: (key: keyof Register, value: string) => void
}

export const StepTwo = ({ item, callback }: StepOneProps) => {
    const { confirm } = useConfirmV2()

    const [name, setName] = useState(item.name)
    const [nameError, setNameError] = useState('')
    const [checkedName, setCheckedName] = useState(false)

    const [email, setEmail] = useState(item.email)
    const [emailError, setEmailError] = useState('')
    const [checkedEmail, setCheckedEmail] = useState(false)

    const [password, setPassword] = useState(item.password)
    const [passwordError, setPasswordError] = useState('')

    const [passwordSec, setPasswordSec] = useState('')
    const [passwordSecError, setPasswordSecError] = useState('')

    const [checkedPassword, setCheckedPassword] = useState(false)

    const nicknameHandler = async () => {
        if (nameError) return

        const newName = name.replace(spaceRegex, '')
        if (!newName || newName.length < 3 || newName.length > 20) {
            setNameError('3~20자를 입력해주세요')
            return
        }
        try {
            // 이름 중복 확인 API 호출
            const { data } = await postCheckName({ name: newName })
            if (!data.data) return // 데이터가 없다면 함수 종료

            // 이름이 사용 가능하지 않은 경우 오류 상태 업데이트
            if (!data.data.available) {
                setNameError('이미 사용중인 이름입니다.')
                setCheckedName(false)
                callback('name', '')
            } else {
                setCheckedName(true)
                setNameError('')
                callback('name', newName)
            }
            setName(newName)
        } catch (err: any) {
            // 오류 발생 시 사용자에게 오류 메시지 표시
            confirm({
                invisibleCancel: true,
                description: err.message,
            })
        }
    }

    const emailHandler = async () => {
        const result =
            !/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/.test(
                email
            )
        if (result) {
            setEmailError('올바른 이메일 형식이 아닙니다.')
            return
        }
        const { data } = await postCheckEmail({ email, type: 'all' })
        if (!data.data?.available) {
            setEmailError('이미 사용중인 이메일입니다.')
            return
        }
        setEmailError('')
        setCheckedEmail(true)
        callback('email', email)
    }

    const validatePasswordHandler = (password: string) => {
        const result = validatePassword(password)
        if (result.passwordLength) {
            setPasswordError('8~18자를 입력해주세요.')
            return false
        }
        if (result.passwordIncludeNumber) {
            setPasswordError('숫자 1자 이상 포함해주세요.')
            return false
        }
        if (result.passwordSpecialString) {
            setPasswordError('특수문자 1자 이상 포함해주세요.')
            return false
        }
        if (result.passwordUpperLowercase) {
            setPasswordError('대소문 구분하여 각 1자 이상 포함해주세요.')
            return false
        }
        if (result.passwordNotIncludeString) {
            setPasswordError('사용할 수 없는 문자를 포함하고 있습니다.')
            return false
        }
        return true
    }

    const passwordHandler = () => {
        if (passwordError) return false
        const valid = validatePasswordHandler(password)
        if (valid) {
            setCheckedPassword(true)
            setPasswordError('')
            passwordSameHandler()
        }
    }

    const passwordSameHandler = () => {
        if (passwordSec !== password) {
            callback('password', '')
        } else {
            const valid = validatePasswordHandler(passwordSec)
            if (valid) {
                callback('password', password)
                setPasswordSecError('')
            }
        }
    }

    return (
        <section className="flex flex-col gap-10 w-full items-center">
            <article className="flex flex-col gap-1">
                <h2
                    className={classNameJoin([
                        'text-[clamp(18px,3vw,22px)] font-bold text-[#242424]',
                    ])}
                >
                    셀러 정보를 모두 입력해주세요.
                </h2>
                <p
                    className={classNameJoin([
                        'text-[clamp(14px,3vw,18px)] font-normal text-[#898989]',
                        'break-keep',
                    ])}
                >
                    클릭메이트에서 활동할 닉네임과 사용할 로그인 계정으로, 입점 이후에도 수정하실 수
                    있어요.
                </p>
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    셀러 닉네임
                </label>
                <div className="flex flex-row gap-2 w-full">
                    <Input
                        type="text"
                        value={name}
                        className="w-full h-[clamp(44px,3vw,52px)]"
                        inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                        placeholder="닉네임 입력"
                        onChange={(e) => {
                            const value = e.target.value
                            if (value.length > 20) {
                                setName(value.slice(0, 20))
                                return
                            }
                            setName(value)
                            setNameError('')
                            setCheckedName(false)
                            callback('name', '')
                        }}
                        onBlur={() => {
                            if (name.length < 3 || name.length > 20) {
                                setNameError('3~20자를 입력해주세요.')
                                return
                            }
                            if (!isValidNickname(name.replace(spaceRegex, ''))) {
                                setNameError('한글만 입력할 수 있어요. (영어/숫자/특수문자 불가)')
                                return
                            }
                            setNameError('')
                        }}
                    />
                    <Button
                        onClick={() => nicknameHandler()}
                        ghost={checkedName}
                        className="w-[24%] h-full flex items-center justify-center"
                    >
                        <p className="text-[clamp(14px,3vw,20px)]">
                            {checkedName ? '확인 완료' : '중복 확인'}
                        </p>
                    </Button>
                </div>
                {nameError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">{nameError}</p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    이메일
                </label>
                <Input
                    type="text"
                    value={email}
                    className="w-full h-[clamp(44px,3vw,52px)]"
                    inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                    placeholder="이메일 입력 (로그인할 때 사용돼요)"
                    onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9a-zA-Z@.]/g, '').slice(0, 100)
                        setEmail(value)
                        setEmailError('')
                        setCheckedEmail(false)
                        callback('email', '')
                    }}
                    onBlur={() => {
                        if (emailError) return
                        emailHandler()
                    }}
                />
                {emailError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {emailError}
                    </p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    비밀번호
                </label>
                <Password
                    autoComplete="new-password"
                    type="password"
                    value={password}
                    className="w-full h-[clamp(44px,3vw,52px)]"
                    inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                    placeholder="영문, 숫자, 특수문자를 포함해 8~18자로 입력"
                    onChange={(e) => {
                        const value = e.target.value

                        setPassword(value)
                        setPasswordError('')
                        setCheckedPassword(false)
                        passwordSameHandler()
                    }}
                    onBlur={() => {
                        if (passwordError) return
                        passwordHandler()
                    }}
                />
                {passwordError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {passwordError}
                    </p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    비밀번호 확인
                </label>
                <Password
                    autoComplete="new-password"
                    value={passwordSec}
                    type="password"
                    className="w-full h-[clamp(44px,3vw,52px)]"
                    inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                    placeholder="비밀번호 한 번 더 입력"
                    onChange={(e) => {
                        const value = e.target.value
                        setPasswordSec(value)
                        setPasswordSecError('')
                        setCheckedPassword(false)
                        passwordSameHandler()
                    }}
                    onBlur={() => {
                        passwordSameHandler()
                        if (passwordSec !== password) {
                            setPasswordSecError('비밀번호가 일치하지 않습니다.')
                            return
                        }
                        setPasswordSecError('')
                    }}
                />
                {passwordSecError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {passwordSecError}
                    </p>
                )}
            </article>
        </section>
    )
}
